import { useCallback, useEffect, useRef, useState } from "react";

import { catalogFilters, historyOptions } from "../useLearning";
import { CATALOG_SOURCE } from "./catalogText";

const EMPTY_HISTORY_OPTIONS = Object.freeze({ kinds: [], undo_reasons: [], statuses: [] });
const NO_PRODUCTS = Object.freeze([]);

const optionsOf = (data) => ({
  kinds: (data && data.kinds) || [],
  undo_reasons: (data && data.undo_reasons) || [],
  statuses: (data && data.statuses) || [],
});

/**
 * The Learning Catalog page's dropdown choices, loaded once per page and shared by both views:
 * - `products`: the product -> category dropdowns of the products tables, with counts of manual entries in `manualState`
 *   (the Manual view shows the counts, the Conversations view only the names). Loaded again only when `manualState` changes
 *   or `refreshProducts()` is called (after a write changed the counts), never on a filter change or a view switch.
 * - `historyOptions` ({kinds, undo_reasons, statuses}): the server's choices and labels, loaded the first time the
 *   Conversations view is shown (`historyShown`), then kept; a failed load is tried again on the next show.
 * Either failing leaves its dropdowns as they were (empty at first); the lists report the store's state on their own.
 */
export default function useCatalogOptions({ manualState, historyShown }) {
  const [products, setProducts] = useState(NO_PRODUCTS);
  const [productsRound, setProductsRound] = useState(0);
  const [options, setOptions] = useState(EMPTY_HISTORY_OPTIONS);
  const optionsLoaded = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    catalogFilters({ source: CATALOG_SOURCE.MANUAL, state: manualState }, { signal })
      .then((data) => { if (!signal.aborted) setProducts((data && data.products) || NO_PRODUCTS); })
      .catch(() => {});   // keep the dropdowns as they were; the list reports the store's state
    return () => controller.abort();
  }, [manualState, productsRound]);

  useEffect(() => {
    if (!historyShown || optionsLoaded.current) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    historyOptions({ signal })
      .then((data) => {
        if (signal.aborted) return;
        optionsLoaded.current = true;
        setOptions(optionsOf(data));
      })
      .catch(() => {});   // not marked loaded: the next show tries again
    return () => controller.abort();
  }, [historyShown]);

  const refreshProducts = useCallback(() => setProductsRound((n) => n + 1), []);
  return { products, historyOptions: options, refreshProducts };
}
